/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useRef, useState, useEffect, useCallback, useReducer, useMemo } from 'react';
import clsx from 'clsx';
import { AEyebrow } from '@marriott/mi-web-ui-kit';
import { AHeader, AHeaderSize, AHeaderTag } from '@marriott/mi-web-ui-kit';
import { ABody, ABodySize, ABodyColor } from '@marriott/mi-web-ui-kit';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import { respGridUtil } from '@marriott/mi-headless-utils';
import { eventUtil } from '@marriott/mi-ui-library';
import * as Styled from './OChat.styles';
import { OChatProps, OChatAlignment, OChatParagraphProps, TEST_ID } from './OChat.types';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { TipSSEClient, GuardrailViolationError } from '../../utils/tipSSEClient';
import { InactivityTimer } from '../../utils/inactivityTimer';
import { handleUnauthorizedError } from '../../utils/authErrorHandler';
import {
  SSEEventManager,
  SSERefs,
  SSEComponentCallbacks,
  SSEConnectionState,
  TipFormattedResponse,
} from '../../utils/sseEventManager';
import { ChatMessage } from '../../molecules/ChatMessage/ChatMessage';
import { OpenHotelPanelOptions } from '../../molecules/MIntegratedImagery/MIntegratedImagery.types';
import { MFieldChat } from '../../molecules/MFieldChat';
import { ChatDisclaimer, type ChatDisclaimerContext, type ChatDisclaimerLayout } from '../../molecules/ChatDisclaimer';
import { PreviewPanel } from '../PreviewPanel/PreviewPanel';
import { EmptyState } from '../EmptyState/EmptyState';
import { StatusIndicator } from '../../atoms/Indicators/StatusIndicator';
import { UnifiedHeader } from '../../molecules/UnifiedHeader/UnifiedHeader';
import { HotelDetailsPanel } from '../HotelDetailsPanel';
import { getPageModel, getMockPageModel, getItemFromPageModel } from '../../utils/helper';
/* eslint-disable @nx/enforce-module-boundaries */
import {
  HQVContext,
  hqvInitialState,
  hqvReducer,
  HQV_ACTIONS,
  useSearchResultsStore as useShopSearchResultsStore,
} from '@marriott/mi-shop-components';
import { useSearchResultsStore } from '../../modules/store/searchResultsStore';
import { usePageContext } from '@marriott/mi-shop-components';
import { fetchAndSetSelectedPropertyData } from '../../modules/utils/ratesHelper';
/* eslint-enable @nx/enforce-module-boundaries */
import {
  HOTEL_DETAILS_PANEL_SEOURL,
  ROUTING_CONFIG,
  SPA_APP_NAME,
  EXPERIENCE_SEGMENT,
  GAURDRAIL_ERROR_MESSAGE,
  PII_DETECTED_ERROR_MESSAGE,
  GUARDRAIL_LIMIT_EXCEEDED_MESSAGE,
  CHAT_DISABLED_PLACEHOLDER,
} from '../../../_constants/constant';
import { DATES, ROOMS_AND_GUESTS, USE_POINTS, SPECIAL_RATES } from '../../store/store.constants';
import { ChatFiltersGlobalStyle } from '../../styled';
import {
  useMarriottAIPreviewStore,
  useChatState,
  useChatActions,
  useChatSessionActions,
  useAPIActions,
  useCurrentView,
  useViewData,
  useViewMode,
  useHoveredHotelId,
  useNavigationActions,
  useItemActions,
  useUIActions,
  useUIState,
  useLoadingMessage,
  useIsChatDisabled,
  AgentExecutionResult,
} from '../../store';
import { LOADING_MESSAGE_TEXT } from '../../utils/loadingMessageUtils';

// Default preview image - shown on page load before user clicks "New Chat"
import DefaultPreviewImage from '../OResults/preview.png';
import { ChatSparklesLottie } from '../../atoms/ChatSparklesLottie';
import { useActiveMessageScroll } from '../../hooks/useActiveMessageScroll';
import { useScrollToBottomIndicator } from '../../hooks/useScrollToBottomIndicator';
import { useSessionUpdate } from '../../hooks/useSessionUpdate';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { resolveChatPreviewImageUrl } from '../../utils/chatPreviewImageUrl';
import {
  useAISearchWelcomePageView,
  useAISearchResultsTracking,
  useAISearchPropertyDetailsPageView,
} from '../../hooks/useAISearchPageView';
import { useAISearchEvent } from '../../hooks/useAISearchEvent';
import { useConversationSearchCriteria } from '../../hooks/useConversationStore';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import { AI_SEARCH_CLICK_TRACK, trackSearchAnalytics, SEARCH_RESULT_UI_TYPES } from '../../utils/acdlUtils';
import { uiContextManager } from '../../utils/uiContextManager';
import { useSearchFormStore } from '../../store/searchFormStore';
import moment from 'moment';
import {
  buildStoreUpdatesFromAmpRequest,
  buildSearchQueryDataFromStore,
  buildChatSearchCriteriaFromAmpRequest,
} from '../../utils/ampRequestMapper';
import { chatResultsMock } from '../../mocks/chatResultsMock';
import { redact } from '../../utils/sensitiveDataRedactor';
import { notifyQualtricsThumbsFeedback } from '../../utils/qualtricsChatFeedback';
import { MAX_CHAT_COUNT } from '../../utils/indexedDBStorage';

const DEFAULT_SSE_BASE_URL = 'https://orchestratorsvc.emergingtech.dev2.cld.marriott.com';
const DEFAULT_INACTIVITY_TIMEOUT_MS = 60 * 1000; // 1 minute fallback
const DEFAULT_EVENT_STREAM_RESPONSE_TIMEOUT_MS = 60 * 1000; // 60 seconds response timeout

export const OChat: FC<OChatProps> = props => {
  const {
    componentId,
    mTextBlockSimple,
    mFieldChat,
    mChipGroup,
    mParagraph,
    mParagraph2,
    className,
    miniSearchBarModel,
    aImage2,
  } = props;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mobileInputRef = useRef<HTMLTextAreaElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);

  // TIP SSE Client and AbortController refs - stored per chatId like ChatUI
  const [tipSSEClient, setTipSSEClient] = useState<TipSSEClient | null>(null);
  const tipAbortControllersByChatRef = useRef<Record<string, AbortController | null>>({});
  const tipConnectionStateByChatRef = useRef<Record<string, SSEConnectionState>>({});
  const tipLastEventIdByChatRef = useRef<Record<string, string>>({});
  const tipInitializingByChatRef = useRef<Record<string, boolean>>({});
  const tipInitializationPromiseByChatRef = useRef<Record<string, Promise<void> | null>>({});
  const pendingStreamClosuresRef = useRef<Set<string>>(new Set());
  const previousChatIdRef = useRef<string | null>(null);
  const inactivityTimerRef = useRef<InactivityTimer | null>(null);
  const eventStreamResponseTimeoutByChatRef = useRef<Record<string, number>>({});
  const tipPendingPreviewByChatRef = useRef<
    Record<
      string,
      {
        viewData: any;
        itemCount: number;
        intent?: string;
        toolsUsed?: string[];
      } | null
    >
  >({});
  const tipUiDataByChatRef = useRef<Record<string, any | null>>({});
  const tipUiTypeByChatRef = useRef<Record<string, string | null>>({});
  const tipFormattedUiDataByChatRef = useRef<Record<string, string | null>>({});
  const tipFormattedResponseByChatRef = useRef<Record<string, TipFormattedResponse | null>>({});
  const sseRetryAttemptRef = useRef<Record<string, boolean>>({});
  const pendingMessageByChatRef = useRef<Record<string, { content: string; uiContext: any } | null>>({});

  // Local processing state - now handled by store
  // const [isProcessing, setIsProcessing] = useState(false);

  // Sidebar, showChat, and isNewChatMode state from Zustand store (shared with ONavigationSide)
  const { setIsSidebarOpen, setShowChat, setIsMobile, setIsNewChatMode } = useUIActions();
  const { isSidebarOpen, showChat, isMobile, isNewChatMode } = useUIState();
  const prevIsNewChatModeRef = useRef(isNewChatMode);

  // Zustand store hooks
  const { inputValue: storeInputValue, messages } = useChatState();
  const {
    addMessage,
    addMessageToChat,
    removeMessage,
    setInputValue: setStoreInputValue,
    navigateToMessagePreview,
    setMessageThumbFeedback,
  } = useChatActions();
  const { createNewChat, setChatTipSession, setLastEventId, updateChatTitle, setLatestErrorMessageId, deleteChat } =
    useChatSessionActions();
  const {
    handleAPIResponse,
    setLoading,
    setIsProcessing,
    setStatus,
    cancelRequest,
    enqueueLoadingMessage,
    clearLoadingMessage,
  } = useAPIActions();
  const currentChatId = useMarriottAIPreviewStore(state => state.currentChatId);
  const chatSessions = useMarriottAIPreviewStore(state => state.chatSessions);
  const currentChat = chatSessions.find(c => c.id === currentChatId);
  const latestErrorMessageId = currentChat?.latestErrorMessageId ?? null;
  const hasHydrated = useMarriottAIPreviewStore(state => state._hasHydrated);
  const isLoading = useMarriottAIPreviewStore(state => state.isLoading);
  const isProcessing = useMarriottAIPreviewStore(state => state.isProcessing);
  const isChatDisabled = useIsChatDisabled();
  const status = useMarriottAIPreviewStore(state => state.status);
  const loadingMessage = useLoadingMessage();
  const setPropertyMediaById = useMarriottAIPreviewStore(state => state.setPropertyMediaById);
  const isAuthenticated = useMarriottAIPreviewStore(state => state.authenticated);

  const emptyStatePreviewImageSrc = useMemo(() => {
    const fromModel = resolveChatPreviewImageUrl(aImage2) ?? '';
    return (
      fromModel || (DefaultPreviewImage as any)?.src || (DefaultPreviewImage as any)?.default || DefaultPreviewImage
    );
  }, [aImage2]);

  // Search form store for syncing dates from NLS response
  const setSearchFormState = useSearchFormStore(state => state.setSearchFormState);
  // Live mini search bar values — read individually to avoid object identity churn
  const searchFormDates = useSearchFormStore((state: any) => state[DATES]);
  const searchFormRooms = useSearchFormStore((state: any) => state[ROOMS_AND_GUESTS]);
  const searchFormUsePoints = useSearchFormStore((state: any) => state[USE_POINTS]);
  const searchFormSpecialRates = useSearchFormStore((state: any) => state[SPECIAL_RATES]);

  // Get cluster code from conversation search criteria for HQV
  const conversationSearchCriteria = useConversationSearchCriteria();

  // Shop results store — used to populate ratesDataForSelectedProperty for the Rates component in HQV
  const setShopSearchResultsState = useShopSearchResultsStore(state => state.setSearchResultsState);
  // Chat components store
  const setSearchResultsState = useSearchResultsStore(state => state.setSearchResultsState);

  const { messagesContainerRef, setMessageNodeRef, prepareActiveMessageScroll, activeMessageSpacerHeight } =
    useActiveMessageScroll({
      messages,
      isProcessing,
      agentStatus: status.status,
    });
  const { showScrollToBottom, scrollToBottom } = useScrollToBottomIndicator(messagesContainerRef, [
    messages,
    isProcessing,
    activeMessageSpacerHeight,
  ]);

  /** Welcome screen only: mobile disclaimer sheet can be dismissed; reset when user leaves welcome. */
  const [welcomeMobileDisclaimerDismissed, setWelcomeMobileDisclaimerDismissed] = useState(false);

  // PreviewPanel hooks
  const currentView = useCurrentView();
  const viewData = useViewData();
  const viewMode = useViewMode();
  const hoveredHotelId = useHoveredHotelId();
  const { updateViewMode, updateViewData } = useNavigationActions();
  const { setHoveredHotelId } = useItemActions();

  // Session update hook for triggering /validate/findHotels at touch points
  const { triggerSessionUpdate } = useSessionUpdate();

  // Extract llmMetadata from viewData for MiniSearchBar date display.
  // Attach payload.query (top-level SSE field) so consumers prefer it over
  // the deeply-nested llmInfo.queryRewrite.
  // Attach isFlexDate so MiniSearchBar can show the flexible date label/tab.
  const llmMetadata = useMemo(() => {
    const meta =
      viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.llmMetadata ?? null;
    const payloadQuery: string | undefined = viewData?.['query'];
    const isFlexDate: boolean = viewData?.['isFlexDate'] === true;
    if (!meta && !payloadQuery && !isFlexDate) return null;
    return { ...(meta ?? {}), payload: { query: payloadQuery }, isFlexDate };
  }, [viewData]);

  // Sync ampRequest from NLS response into the search form store (dates + rooms/guests)
  // and store ampRequest in sessionDataValues for the Rates component (HQV) to use
  useEffect(() => {
    const ampRequest =
      viewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.ampRequest ?? null;
    // Extract isFlexDate from viewData to preserve flexible date state
    const isFlexDate = viewData?.['isFlexDate'] === true;
    const updates = buildStoreUpdatesFromAmpRequest(ampRequest, isFlexDate);
    if (updates) {
      setSearchFormState(updates.keys, updates.data);
    }
    // Store ampRequest in sessionDataValues so Rates component can access it for rateReqTypes
    const storeSessionDataValues = useSearchResultsStore.getState().sessionDataValues;
    const storeShopSessionDataValues = useShopSearchResultsStore.getState().sessionDataValues;
    const existingInnerSessionDataValues = storeSessionDataValues?.sessionDataValues ?? {};
    const existingInnerShopSessionDataValues = storeShopSessionDataValues?.sessionDataValues ?? {};
    setSearchResultsState(['sessionDataValues'], {
      ...storeSessionDataValues,
      ampRequest,
      sessionDataValues: {
        ...existingInnerSessionDataValues,
        ampRequest,
      },
    });
    setShopSearchResultsState(['sessionDataValues'], {
      ...storeShopSessionDataValues,
      ampRequest,
      sessionDataValues: {
        ...existingInnerShopSessionDataValues,
        ampRequest,
      },
    });
  }, [viewData, setSearchFormState, setSearchResultsState, setShopSearchResultsState]);

  // HQV (Hotel Quick View) state
  const [hqvState, hqvDispatch] = useReducer(hqvReducer, hqvInitialState);
  const [responsiveGridModelHotelDetails, setResponsiveGridModelHotelDetails] = useState<any>({});
  const [responsiveGridModelPropsHotelDetails, setResponsiveGridModelPropsHotelDetails] = useState<any>({});

  // Hotel details panel state
  const [isHotelPanelOpen, setIsHotelPanelOpen] = useState(false);
  const [selectedHotelData, setSelectedHotelData] = useState<any>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  // Tracks whether the property currently shown in HQV came from a single-property
  // response (e.g. availability_calendar_search). This must reflect the specific
  // message the user clicked, NOT the global current preview state, so that clicking
  // a hotel from an older multi-property message still shows rates correctly.
  const [selectedIsSinglePropertyResponse, setSelectedIsSinglePropertyResponse] = useState(false);

  // Get values from PageContext for HQV
  const {
    currentLocale,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    requestId,
    cookies,
    sessionData,
    interceptorResponse,
  } = usePageContext();
  const IS_LOCAL_DEV = process.env.NODE_ENV === 'development';

  const sessionDataObj = sessionData?.data ? sessionData?.data : sessionData?.cacheData?.data;
  const sessionID = sessionData?.key ?? sessionDataObj?.sessionToken;

  // Extract current location from interceptorResponse for personalization
  const geoParams = interceptorResponse?.requestAttributes?.geoLocationParams;
  const currentLocation =
    geoParams && (geoParams.city || geoParams.region_code || geoParams.country_code)
      ? {
          city: geoParams.city,
          state: geoParams.region_code,
          country: geoParams.country_code,
        }
      : undefined;

  // Local state for input value (fallback when store is not available)
  const [localInputValue, setLocalInputValue] = useState('');
  const [hasPiiDetected, setHasPiiDetected] = useState(false);
  // Track raw (unredacted) input separately to fix incremental typing redaction (WEB-193725)
  const rawInputRef = useRef<string>('');

  // Use store value if available, otherwise use local state
  const inputValue = storeInputValue || localInputValue;

  // Extract static props from mFieldChat (not messaging callbacks)
  const configuredMaxLength = mFieldChat?.maxLength;
  const maxLength = Math.min(configuredMaxLength ?? 600, 600);

  const alignment = mTextBlockSimple?.alignment || OChatAlignment.Center;
  const alignmentClass = alignment === OChatAlignment.Center ? 'text-center' : 'text-left';

  /**
   * ACDL PAGEVIEW Tracking for "AI Search|Welcome" (Authenticated)
   * Fires when authenticated users see the welcome screen (no messages)
   * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
   */
  const { trackPageView: trackWelcomePageView, isDataLayerReady } = useAISearchWelcomePageView(false);

  /**
   * ACDL SEARCH Event Tracking for search results
   * Fires when search results are returned from the AI
   * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/2345959449
   */
  const { trackSearchEvent } = useAISearchEvent();

  /**
   * ACDL PAGEVIEW Tracking for "AI Search|Search Results" and edge cases
   * Fires when search results are displayed to the user
   * Uses UXL uiType to determine the correct page name and template
   * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
   */
  const { trackSearchResultsPageView } = useAISearchResultsTracking();

  /**
   * ACDL PAGEVIEW Tracking for "AI Search|Property Details"
   * Fires when the Hotel Quick View (HQV) panel is opened
   * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
   */
  const { trackPageView: trackPropertyDetailsPageView } = useAISearchPropertyDetailsPageView(false);

  /**
   * Track "AI Search|Welcome" PAGEVIEW when authenticated user sees welcome screen
   * Only fires when:
   * - User is authenticated
   * - Welcome screen is shown (no messages)
   * - Data layer is ready
   */
  const hasTrackedWelcomeRef = useRef(false);
  useEffect(() => {
    const shouldTrackWelcome = isAuthenticated && messages.length === 0 && isDataLayerReady;

    if (shouldTrackWelcome && !hasTrackedWelcomeRef.current) {
      hasTrackedWelcomeRef.current = true;
      trackWelcomePageView();
    }

    // Reset tracking flag when messages are cleared (new chat)
    if (messages.length > 0) {
      hasTrackedWelcomeRef.current = false;
    }
  }, [isAuthenticated, messages.length, isDataLayerReady, trackWelcomePageView]);

  /**
   * ACDL SEARCH + Search Results PAGEVIEW tracking, driven by the SSE response.
   *
   * Rather than passing tracking callbacks into the memoized SSE event manager
   * (which captured a stale `datalayer` closure and silently dropped events), we
   * react to the assistant message that the SSE response writes into the store.
   * This keeps the live `useSession()` datalayer and avoids any memoization issues.
   *
   * Fires once per assistant search-result message (deduped by message id) for every
   * search, regardless of whether results were returned (per tracking requirements).
   */
  const trackedSearchMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDataLayerReady) return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.isUser || !lastMessage.uiType) return;

    // Only count search-result responses (the uiTypes that map to an AI Search page).
    if (!SEARCH_RESULT_UI_TYPES.has(lastMessage.uiType)) return;

    // Guardrail-blocked responses reuse the `default` uiType but are not searches —
    // exclude them so we don't over-count (matches the prior SSE-manager behavior).
    if (lastMessage.intent === 'guardrail_blocked') return;

    // Dedupe: never track the same assistant message twice.
    if (trackedSearchMessageIdRef.current === lastMessage.id) return;
    trackedSearchMessageIdRef.current = lastMessage.id;

    trackSearchAnalytics({ trackSearchResultsPageView, trackSearchEvent }, lastMessage.uiType, {
      viewData: lastMessage.previewData?.data as any,
      itemCount: lastMessage.previewData?.itemCount ?? 0,
    });
  }, [messages, isDataLayerReady, trackSearchResultsPageView, trackSearchEvent]);

  useEffect(() => {
    if (messages.length > 0) {
      setWelcomeMobileDisclaimerDismissed(false);
      return;
    }
    const showWelcomeSheet =
      !isNewChatMode && showChat && !welcomeMobileDisclaimerDismissed && mParagraph?.aBody?.enabled !== false;
    if (!showWelcomeSheet) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setWelcomeMobileDisclaimerDismissed(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [messages.length, isNewChatMode, showChat, welcomeMobileDisclaimerDismissed, mParagraph?.aBody?.enabled]);

  // Close hotel details panel when starting a new chat
  useEffect(() => {
    if (isNewChatMode) {
      setIsHotelPanelOpen(false);
    }
  }, [isNewChatMode]);

  // Effect to sync chat header position with main header - uses direct DOM manipulation for instant response
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Track previous state to determine transition direction
    let wasBelow = true;

    const updateChatHeaderPosition = () => {
      // Only apply on mobile
      if (window.innerWidth >= 992) {
        return;
      }

      // Main header height varies by viewport width
      const mainHeaderHeight = window.innerWidth >= 576 ? 88 : 52;

      // Check if at very top of page (main header in normal document flow)
      const isAtTop = window.scrollY < 10;

      // Check if main header is sticky (scrolling down)
      const header = document.querySelector('[data-component-name="o-common-static-navigationcontainer"]');
      const isSticky = header?.classList.contains('header-sticky') ?? false;

      // Directly manipulate the chat header's transform for instant response
      const chatHeader = document.querySelector('[data-chat-header="mobile"]') as HTMLElement;
      if (chatHeader) {
        // Chat header should be below main header when:
        // 1. At top of page (main header in normal flow), OR
        // 2. Main header is sticky (scrolling down)
        const shouldBeBelow = isAtTop || isSticky;

        // Add transition when moving down (main header appearing), instant when moving up
        if (shouldBeBelow && !wasBelow) {
          // Moving down - add transition to match main header's slideIn animation
          chatHeader.style.transition = 'transform 0.4s ease';
        } else if (!shouldBeBelow && wasBelow) {
          // Moving up - instant (no transition)
          chatHeader.style.transition = 'none';
        }

        chatHeader.style.transform = shouldBeBelow ? `translateY(${mainHeaderHeight}px)` : 'translateY(0)';
        wasBelow = shouldBeBelow;
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 992) {
        // Reset to CSS default on desktop
        const chatHeader = document.querySelector('[data-chat-header="mobile"]') as HTMLElement;
        if (chatHeader) {
          chatHeader.style.transform = '';
          chatHeader.style.transition = '';
        }
      } else {
        updateChatHeaderPosition();
      }
    };

    // Initial position
    updateChatHeaderPosition();

    window.addEventListener('scroll', updateChatHeaderPosition, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateChatHeaderPosition);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // WEB-192990 G-02 / G-05: move focus to "Where can we take you?" after New Chat (sidebar or header)
  useEffect(() => {
    const enteredNewChatMode = isNewChatMode && !prevIsNewChatModeRef.current;
    prevIsNewChatModeRef.current = isNewChatMode;

    if (!enteredNewChatMode) {
      return;
    }

    let outerFrameId = 0;
    let innerFrameId = 0;
    outerFrameId = window.requestAnimationFrame(() => {
      innerFrameId = window.requestAnimationFrame(() => {
        const chatInput = mobileInputRef.current ?? inputRef.current;
        chatInput?.focus();
      });
    });

    return () => {
      window.cancelAnimationFrame(outerFrameId);
      window.cancelAnimationFrame(innerFrameId);
    };
  }, [isNewChatMode]);

  // Keep mobile flag in sync when OChat is rendered standalone.
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsMobile]);

  // Initialize TIP SSE Client
  useEffect(() => {
    const tipURL = useClientEnvVarsStore.getState().envVarsObject['TIP_SSE_ENDPOINT'] || DEFAULT_SSE_BASE_URL;
    const tipSessionURL = useClientEnvVarsStore.getState().envVarsObject['TIP_SESSION_ENDPOINT'] || '';
    const isLocalDev = useClientEnvVarsStore.getState().envVarsObject['IS_LOCAL_DEV'] === 'true';
    setTipSSEClient(
      new TipSSEClient(
        tipURL,
        tipSessionURL,
        requestId,
        cookies?.['authStateToken'] || '',
        isLocalDev,
        sessionDataObj?.memberLevel,
        currentLocation
      )
    );
  }, []);

  // Cleanup AbortControllers on unmount
  useEffect(() => {
    return () => {
      // Persist all lastEventIds before cleanup (for reconnection on remount)
      Object.entries(tipLastEventIdByChatRef.current).forEach(([chatId, eventId]) => {
        if (eventId) {
          useMarriottAIPreviewStore.getState().setLastEventId(chatId, eventId);
        }
      });
      tipLastEventIdByChatRef.current = {};

      // Clear all response timeout timers
      Object.values(eventStreamResponseTimeoutByChatRef.current).forEach(timerId => {
        clearTimeout(timerId);
      });
      eventStreamResponseTimeoutByChatRef.current = {};

      // Abort all SSE connections for all chats
      Object.values(tipAbortControllersByChatRef.current).forEach(ctrl => {
        try {
          ctrl?.abort();
        } catch {
          // ignore
        }
      });
      tipAbortControllersByChatRef.current = {};
      tipConnectionStateByChatRef.current = {};
    };
  }, []);

  // Fetch HQV (Hotel Quick View) AEM model on mount
  useEffect(() => {
    (async function () {
      const getCMSTemplateReqBody = {
        requestType: ROUTING_CONFIG,
        seoUrl: HOTEL_DETAILS_PANEL_SEOURL,
        localeKey: currentLocale,
        experienceSegment: EXPERIENCE_SEGMENT,
        applicationName: SPA_APP_NAME,
        sessionToken: sessionID,
      };

      let modelTemplate: any = {};
      if (IS_LOCAL_DEV === true) {
        modelTemplate = await getMockPageModel('HotelDetails');
      } else {
        modelTemplate = await getPageModel(getCMSTemplateReqBody, NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT);
      }
      const responsiveGridModelHotelDetailsResp = getItemFromPageModel(modelTemplate, 'root/responsivegrid');
      setResponsiveGridModelHotelDetails(responsiveGridModelHotelDetailsResp);
      setResponsiveGridModelPropsHotelDetails(respGridUtil(responsiveGridModelHotelDetailsResp));
    })();
  }, [currentLocale, sessionID]);

  // Preload map component bundle in background for faster "New Chat" transition
  // This fetches the ChatMapViewContainer chunk before user clicks "New Chat"
  useEffect(() => {
    // Use dynamic import to preload the map component bundle
    // Using relative path to avoid TypeScript module resolution issues
    import('../../molecules/ChatMapView/ChatMapViewContainer').catch(() => {
      // Silently ignore preload failures - map will load normally when needed
    });
  }, []);

  // Handle navigation to message preview
  const handleNavigateToMessage = useCallback(
    (messageId: string) => {
      navigateToMessagePreview(messageId);
    },
    [navigateToMessagePreview]
  );

  const handleThumbFeedback = useCallback(
    (messageId: string, direction: 'up' | 'down') => {
      const currentTipWorkflowId = currentChat?.tipWorkflowId ?? '';
      if (!(currentTipWorkflowId && currentChatId)) return;
      setMessageThumbFeedback(currentChatId, messageId, direction);
      notifyQualtricsThumbsFeedback(direction, currentTipWorkflowId, messageId);
    },
    [currentChat?.tipWorkflowId, currentChatId, setMessageThumbFeedback]
  );

  // PreviewPanel handlers
  const handleViewToggle = useCallback(
    (view: 'grid' | 'map') => {
      updateViewMode(view);
      // Only trigger session update if we have search results
      // (viewData indicates we have results to display)
      const currentViewData = useMarriottAIPreviewStore.getState().currentState?.data;
      if (currentViewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges?.length) {
        triggerSessionUpdate('view_mode_change');
      }
    },
    [updateViewMode, triggerSessionUpdate]
  );

  // Handle NLS search completion (filters applied)
  const handleNLSSearchComplete = useCallback(
    (_result: any) => {
      // Trigger session update when filters are applied and NLS search completes
      triggerSessionUpdate('filter_applied');
    },
    [triggerSessionUpdate]
  );

  const syncChatSearchCriteria = useCallback(() => {
    // Read directly from the store to avoid stale closures
    const currentDates = useSearchFormStore.getState()[DATES];
    const currentRooms = useSearchFormStore.getState()[ROOMS_AND_GUESTS];
    const currentUsePoints = useSearchFormStore.getState()[USE_POINTS];
    const currentSpecialRates = useSearchFormStore.getState()[SPECIAL_RATES];
    const currentChatIdFromStore = useMarriottAIPreviewStore.getState().currentChatId;

    // Keep chat search criteria in shop store even before HQV opens so direct links
    // can build chat-aware URLs on first click. Only write when values actually change.
    const checkIn = currentDates?.fromDate
      ? moment(currentDates.fromDate).format('YYYY-MM-DD')
      : currentDates?.checkInDate ?? undefined;
    const checkOut = currentDates?.toDate
      ? moment(currentDates.toDate).format('YYYY-MM-DD')
      : currentDates?.checkOutDate ?? undefined;
    const isFlexible = currentDates?.flexible as string | boolean | undefined;
    // Flexible date search is determined by the flexible flag from the store,
    // which is set from the NLS response (isFlexDate). A flexible search can
    // have dates (earliest/latest) but still be marked as flexible.
    const flexibleDate = isFlexible === 'true' || isFlexible === true;
    // DEBUG: Log the flexible date determination in syncChatSearchCriteria
    console.log('[syncChatSearchCriteria] flexibleDate debug:', {
      'currentDates?.flexible': currentDates?.flexible,
      isFlexible,
      'flexibleDate (computed)': flexibleDate,
    });
    const nextCriteria = {
      checkInDate: checkIn,
      checkOutDate: checkOut,
      flexibleDate,
      numRooms: currentRooms?.numRooms ?? 1,
      numAdultsPerRoom: currentRooms?.numAdultsPerRoom ?? 1,
      numChildrenPerRoom: currentRooms?.numChildrenPerRoom ?? 0,
      childrenAges: currentRooms?.childrenAges ?? [],
      useRewardsPoints: currentUsePoints?.rewardsRedemption ?? false,
      clusterCode: currentSpecialRates?.clusterCode ?? '',
      corporateCode: currentSpecialRates?.corporateCode ?? '',
      groupCode: currentSpecialRates?.groupCode ?? '',
    };

    const serialized = JSON.stringify(nextCriteria);
    const storeSessionDataValues = useSearchResultsStore.getState().sessionDataValues;
    const storeShopSessionDataValues = useShopSearchResultsStore.getState().sessionDataValues;
    const existingSerialized = JSON.stringify(storeSessionDataValues?.chatSearchCriteria ?? {});
    const _existingShopSerialized = JSON.stringify(storeShopSessionDataValues?.chatSearchCriteria ?? {});
    if (serialized === existingSerialized) return;

    const existingInnerSessionDataValues = storeSessionDataValues?.sessionDataValues ?? {};
    const existingInnerShopSessionDataValues = storeShopSessionDataValues?.sessionDataValues ?? {};
    setSearchResultsState(['sessionDataValues'], {
      // Keep top-level key for components that read directly from sessionDataValues.chatSearchCriteria
      chatSearchCriteria: nextCriteria,
      // Preserve existing inner sessionDataValues keys such as isFlexibleSearch/recentlyViewedPropertyCode
      sessionDataValues: {
        ...existingInnerSessionDataValues,
        chatSearchCriteria: nextCriteria,
      },
    });
    setShopSearchResultsState(['sessionDataValues'], {
      ...storeShopSessionDataValues,
      chatSearchCriteria: nextCriteria,
      sessionDataValues: {
        ...existingInnerShopSessionDataValues,
        chatSearchCriteria: nextCriteria,
      },
    });

    // Also update the per-conversation search state in the chat store
    if (currentChatIdFromStore) {
      useMarriottAIPreviewStore.getState().setConversationSearchCriteria(currentChatIdFromStore, {
        checkInDate: checkIn,
        checkOutDate: checkOut,
        flexible: flexibleDate,
        numRooms: currentRooms?.numRooms ?? 1,
        numAdultsPerRoom: currentRooms?.numAdultsPerRoom ?? 1,
        numChildrenPerRoom: currentRooms?.numChildrenPerRoom ?? 0,
        childrenAges: currentRooms?.childrenAges ?? [],
        useRewardsPoints: currentUsePoints?.rewardsRedemption ?? false,
        clusterCode: currentSpecialRates?.clusterCode ?? '',
        corporateCode: currentSpecialRates?.corporateCode ?? '',
        groupCode: currentSpecialRates?.groupCode ?? '',
      });

      // NOTE: Session update is NOT triggered here on mini search criteria changes.
      // The validate/findHotels call should only happen AFTER search results are retrieved,
      // which is handled by handleResponseComplete ('new_search_results') and
      // handleNLSSearchComplete ('filter_applied').
    }
  }, [setSearchResultsState, setShopSearchResultsState]);

  useEffect(() => {
    syncChatSearchCriteria();
  }, [syncChatSearchCriteria, searchFormDates, searchFormRooms, searchFormUsePoints, searchFormSpecialRates]);

  const handleOpenHotelPanel = useCallback(
    (propertyData: any, propertyId: string, options?: OpenHotelPanelOptions) => {
      // Use message-specific viewData if provided, otherwise fall back to current store state.
      // This ensures prices displayed in HQV match the search criteria that was active
      // when the message was created, preventing price inconsistency when users
      // modify search criteria or view older messages.
      const viewDataToUse = options?.messageViewData ?? useMarriottAIPreviewStore.getState().currentState?.data;

      // Determine whether THIS panel should show single-property behavior (hide rates,
      // show "View Rates" button). This is based on the data of the specific message/view
      // the user clicked, not the global current preview state. Clicking a hotel from an
      // older multi-property message must show rates even if the most recent response was
      // a single-property (availability_calendar_search) result.
      setSelectedIsSinglePropertyResponse(viewDataToUse?.isSinglePropertyResponse === true);

      // Populate the shop Zustand store so the Rates component inside HQV can
      // resolve propertyData (basicInformation.bookable etc.) from the SSE edges.
      const edges = viewDataToUse?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges;
      if (edges) {
        fetchAndSetSelectedPropertyData(
          setShopSearchResultsState,
          setSearchResultsState,
          { edges },
          propertyId || propertyData?.propertyId,
          false,
          ''
        );
      }

      // NOTE: We intentionally do NOT update setSearchFormState here when opening HQV
      // from a property link click. The MiniSearchBar should only be updated when
      // "See All Properties" is clicked (handled in handleViewAllProperties).
      // For HQV, we use chatSearchCriteria in sessionDataValues for URL construction.

      // When HQV is opened from a chat summary (messageViewData provided), use the message's
      // search criteria for URL construction. When opened from list/map view (no messageViewData),
      // use the current global search criteria from the mini search panel.
      if (options?.messageViewData) {
        // Extract search criteria from the message's ampRequest and llmMetadata
        const nlsQuery =
          options.messageViewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery;
        const ampRequest = nlsQuery?.ampRequest;
        const llmMetadata = nlsQuery?.llmMetadata;
        const isFlexDate = options.messageViewData?.isFlexDate === true;

        const messageSearchCriteria = buildChatSearchCriteriaFromAmpRequest(ampRequest, llmMetadata, isFlexDate);

        if (messageSearchCriteria) {
          // Temporarily set the message-specific search criteria in the store
          // This ensures HQV's "View Rates" URLs use the correct dates/criteria
          const storeSessionDataValues = useSearchResultsStore.getState().sessionDataValues;
          const storeShopSessionDataValues = useShopSearchResultsStore.getState().sessionDataValues;
          const existingInnerSessionDataValues = storeSessionDataValues?.sessionDataValues ?? {};
          const existingInnerShopSessionDataValues = storeShopSessionDataValues?.sessionDataValues ?? {};

          // Also set the ampRequest so the Rates component can build correct rateReqTypes
          // This is critical for showing the correct rate when clicking on older messages
          setSearchResultsState(['sessionDataValues'], {
            ...storeSessionDataValues,
            ampRequest,
            chatSearchCriteria: messageSearchCriteria,
            sessionDataValues: {
              ...existingInnerSessionDataValues,
              ampRequest,
              chatSearchCriteria: messageSearchCriteria,
            },
          });
          setShopSearchResultsState(['sessionDataValues'], {
            ...storeShopSessionDataValues,
            ampRequest,
            chatSearchCriteria: messageSearchCriteria,
            sessionDataValues: {
              ...existingInnerShopSessionDataValues,
              ampRequest,
              chatSearchCriteria: messageSearchCriteria,
            },
          });
        }
      } else {
        // No messageViewData - HQV opened from list/map view
        // Ensure latest mini-search values are synced before opening HQV
        syncChatSearchCriteria();
      }

      // Read latest propertyMediaById from store
      const currentPropertyMediaById = useMarriottAIPreviewStore.getState().propertyMediaById;

      // Read the latest includeTaxesAndFees value from the conversation filter state
      const currentChatIdFromStore = useMarriottAIPreviewStore.getState().currentChatId;
      const currentChat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === currentChatIdFromStore);
      const currentIncludeTaxesAndFees =
        currentChat?.conversationSearchState?.filterState?.includeTaxesAndFees ?? false;

      // Set property session data for HQV components (HotelGalleryTitle, etc.) to access search criteria
      // This is required for the HQV components to make proper GraphQL calls
      // Merge cluster code and showFullPrice from conversation filter state into session data
      const sessionDataWithClusterCode = sessionData
        ? {
            ...sessionData,
            cacheData: sessionData.cacheData
              ? {
                  ...sessionData.cacheData,
                  data: sessionData.cacheData.data
                    ? {
                        ...sessionData.cacheData.data,
                        AriesCommon: {
                          ...sessionData.cacheData.data.AriesCommon,
                          rk_clusterCode:
                            conversationSearchCriteria.clusterCode ||
                            sessionData.cacheData.data.AriesCommon?.rk_clusterCode,
                        },
                        AriesSearch: {
                          ...sessionData.cacheData.data.AriesSearch,
                          searchCriteria: {
                            ...sessionData.cacheData.data.AriesSearch?.searchCriteria,
                            showFullPrice: currentIncludeTaxesAndFees
                              ? 'true'
                              : sessionData.cacheData.data.AriesSearch?.searchCriteria?.showFullPrice ?? 'false',
                          },
                        },
                      }
                    : sessionData.cacheData.data,
                }
              : sessionData.cacheData,
          }
        : sessionData;
      hqvDispatch({
        type: HQV_ACTIONS.PROPERTY_SESSION_DATA,
        payload: sessionDataWithClusterCode,
      });
      hqvDispatch({
        type: HQV_ACTIONS.PROPERTY_IDENTITY,
        payload: propertyId || propertyData?.propertyId,
      });
      hqvDispatch({
        type: HQV_ACTIONS.MODAL_OPEN_STATE,
        payload: true,
      });
      hqvDispatch({
        type: HQV_ACTIONS.OPEN_GALLERY_ON_LOAD,
        payload: Boolean(options?.openGallery),
      });
      hqvDispatch({
        type: HQV_ACTIONS.CACHED_PROPERTY_MEDIA,
        payload: currentPropertyMediaById?.[propertyId || propertyData?.propertyId] ?? undefined,
      });
      setSelectedHotelData(propertyData);
      setSelectedPropertyId(propertyId);
      setIsHotelPanelOpen(true);

      // Track HQV panel open state for ui_context
      uiContextManager.setHqvPanelOpen(true);

      // Clear any active marker / cluster hover so MMapPinHoverOverlay (z-index 1400)
      // doesn't paint on top of the HQV. Reuses ChatMapView's onCardOut listener.
      eventUtil.dispatch('propCardUnOver', null);

      // Sync TIP LLM with the property the user is now viewing
      uiContextManager.setState('property_details_view', {
        ...(propertyData ?? {}),
        property_id: propertyId || propertyData?.propertyId,
        property_name:
          propertyData?.name ||
          propertyData?.basicInformation?.name ||
          propertyData?.hotelName ||
          propertyData?.property_name,
      });

      // Track "AI Search|Property Details" PAGEVIEW when HQV panel is opened
      trackPropertyDetailsPageView();
    },
    [
      hqvDispatch,
      trackPropertyDetailsPageView,
      setSearchResultsState,
      setShopSearchResultsState,
      syncChatSearchCriteria,
      sessionData,
      conversationSearchCriteria,
    ]
  );

  const handleCloseHotelPanel = useCallback(() => {
    hqvDispatch({
      type: HQV_ACTIONS.MODAL_OPEN_STATE,
      payload: false,
    });
    hqvDispatch({
      type: HQV_ACTIONS.OPEN_GALLERY_ON_LOAD,
      payload: false,
    });
    hqvDispatch({
      type: HQV_ACTIONS.CACHED_PROPERTY_MEDIA,
      payload: undefined,
    });
    setIsHotelPanelOpen(false);
    setSelectedHotelData(null);
    setSelectedPropertyId(null);
    // Track HQV panel closed state and restore search context if available
    uiContextManager.setHqvPanelOpen(false);
    uiContextManager.clearHqvContext();
  }, [hqvDispatch]);

  // Reuse already-fetched preview media in chat cards by caching by property ID.
  const handlePropertyMediaLoaded = useCallback(
    (propertiesMedia: any[]) => {
      setPropertyMediaById(propertiesMedia);
    },
    [setPropertyMediaById]
  );

  // UnifiedHeader handlers
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen, setIsSidebarOpen]);

  const handleMobileToggle = useCallback(() => {
    setShowChat(!showChat);
  }, [showChat, setShowChat]);

  const handleRenameChat = useCallback(
    (chatId: string, newTitle: string) => {
      updateChatTitle(chatId, newTitle);
    },
    [updateChatTitle]
  );

  const handleDeleteChat = useCallback(
    (chatId: string) => {
      deleteChat(chatId);
    },
    [deleteChat]
  );

  const handleNewChat = () => {
    // Get current chat sessions and filter to non-empty chats
    const chatSessionsState = useMarriottAIPreviewStore.getState().chatSessions;
    const nonEmptyChats = chatSessionsState.filter(chat => chat.messageCount > 0);
    const maxChatCount = Number(useClientEnvVarsStore.getState().envVarsObject['MAX_CHAT_COUNT']) || MAX_CHAT_COUNT;

    // Auto-delete oldest chat if at max capacity
    if (nonEmptyChats.length >= maxChatCount) {
      const oldestChat = [...nonEmptyChats].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )[0];
      if (oldestChat) {
        useMarriottAIPreviewStore.getState().deleteChat(oldestChat.id);
      }
    }

    uiContextManager.clearAllContext();
    setShowChat(true);
    createNewChat();
    setIsNewChatMode(true);
  };

  // Get current chat name for header
  const currentChatName = useMarriottAIPreviewStore(
    state => state.chatSessions.find(c => c.id === state.currentChatId)?.title || 'New Chat'
  );

  const renderEmptyState = useCallback(() => {
    // Use AEM content from mTextBlockSimple and mFieldChat props
    const title = mTextBlockSimple?.aHeader?.text || 'Where can we take you?';
    const description =
      mTextBlockSimple?.aBody?.bodyText ||
      "Ask me about hotels, restaurants, or things to do. I'm here to help plan your perfect trip!";

    return <EmptyState title={title} description={description} />;
  }, [mTextBlockSimple]);

  // Wait for SSE connection to open - now uses connection state ref
  const waitForConnectionOpen = useCallback((chatId: string, timeoutMs = 15000) => {
    if (tipConnectionStateByChatRef.current[chatId] === 'open') return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        const state = tipConnectionStateByChatRef.current[chatId];
        if (state === 'open') {
          clearInterval(checkInterval);
          resolve();
        } else if (state === 'closed' || Date.now() - startTime > timeoutMs) {
          clearInterval(checkInterval);
          // Cleanup on failure
          try {
            tipAbortControllersByChatRef.current[chatId]?.abort();
          } catch {
            // ignore
          }
          tipAbortControllersByChatRef.current[chatId] = null;
          tipConnectionStateByChatRef.current[chatId] = 'closed';
          reject(
            new Error(
              state === 'closed' ? 'SSE connection failed before opening' : `SSE open timeout after ${timeoutMs}ms`
            )
          );
        }
      }, 100);
    });
  }, []);

  // Close SSE stream for a specific chat
  const closeStream = useCallback(
    (chatId: string) => {
      // Persist last event ID to store before closing (for reconnection)
      const lastEventId = tipLastEventIdByChatRef.current[chatId];
      if (lastEventId) {
        setLastEventId(chatId, lastEventId);
        delete tipLastEventIdByChatRef.current[chatId];
      }

      try {
        tipAbortControllersByChatRef.current[chatId]?.abort();
      } catch {
        // ignore
      }
      tipAbortControllersByChatRef.current[chatId] = null;
      tipConnectionStateByChatRef.current[chatId] = 'closed';
      pendingStreamClosuresRef.current.delete(chatId);
    },
    [setLastEventId]
  );

  // Handle response timeout - closes stream and shows error message with retry option
  const handleResponseTimeout = useCallback(
    (chatId: string, originalMessage: string) => {
      // Clear the timeout ref
      delete eventStreamResponseTimeoutByChatRef.current[chatId];

      // Close stream (persists lastEventId)
      closeStream(chatId);

      // Create error message with retry option
      const errorId = Date.now().toString() + '-timeout';
      const errorMessage = {
        id: errorId,
        content: 'Something went wrong.',
        isUser: false,
        timestamp: new Date(),
        toolsUsed: [],
        uiType: 'error',
        uiData: {
          errorText: 'Something went wrong.',
          originalMessage,
          errorMessageId: errorId,
        },
      };
      addMessageToChat(chatId, errorMessage);
      setLatestErrorMessageId(chatId, errorId);

      // Clear processing state
      clearLoadingMessage(chatId);
      setStatus({ status: 'idle' }, chatId);
      setLoading(false);
      setIsProcessing(false, chatId);
    },
    [
      closeStream,
      addMessageToChat,
      setLatestErrorMessageId,
      clearLoadingMessage,
      setStatus,
      setLoading,
      setIsProcessing,
    ]
  );

  // Handle response completion - closes stream if marked for deferred closure
  const handleResponseComplete = useCallback(
    (chatId: string) => {
      // Clear response timeout timer
      if (eventStreamResponseTimeoutByChatRef.current[chatId]) {
        clearTimeout(eventStreamResponseTimeoutByChatRef.current[chatId]);
        delete eventStreamResponseTimeoutByChatRef.current[chatId];
      }

      // Reset inactivity timer - user just received a response
      inactivityTimerRef.current?.resetTimer();

      // Only trigger session update if we have search results
      // Check if the current viewData has search results (edges)
      const currentViewData = useMarriottAIPreviewStore.getState().currentState?.data;
      const hasSearchResults =
        currentViewData?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.edges?.length > 0;

      if (hasSearchResults) {
        // Trigger session update when new search results are received
        triggerSessionUpdate('new_search_results');
      }

      // Close stream if marked for deferred closure
      if (pendingStreamClosuresRef.current.has(chatId)) {
        closeStream(chatId);
      }
    },
    [closeStream, triggerSessionUpdate]
  );

  // SSE refs object for SSEEventManager
  const sseRefs: SSERefs = useMemo(
    () => ({
      pendingPreview: tipPendingPreviewByChatRef,
      uiData: tipUiDataByChatRef,
      uiType: tipUiTypeByChatRef,
      formattedUiData: tipFormattedUiDataByChatRef,
      formattedResponse: tipFormattedResponseByChatRef,
      abortControllers: tipAbortControllersByChatRef,
      connectionState: tipConnectionStateByChatRef,
      lastEventId: tipLastEventIdByChatRef,
    }),
    []
  );

  // SSE component callbacks - only functions that depend on React hooks/context
  const sseComponentCallbacks: SSEComponentCallbacks = useMemo(
    () => ({
      handleCloseHotelPanel,
      onResponseComplete: handleResponseComplete,
      onEventIdReceived: (chatId: string, eventId: string) => {
        tipLastEventIdByChatRef.current[chatId] = eventId;
      },
      onCloseStream: closeStream,
      onAuthenticationFailed: (chatId: string) => {
        const storeState = useMarriottAIPreviewStore.getState();
        const chat = storeState.chatSessions.find(c => c.id === chatId);
        const isProcessing = chat?.isProcessing ?? false;

        closeStream(chatId);

        if (isProcessing) {
          const errorId = Date.now().toString() + '-auth-error';
          addMessageToChat(chatId, {
            id: errorId,
            content: 'Something went wrong.',
            isUser: false,
            timestamp: new Date(),
            toolsUsed: [],
            uiType: 'error',
            uiData: {
              errorText: 'Something went wrong.',
              errorMessageId: errorId,
            },
          });
          setLatestErrorMessageId(chatId, errorId);
          clearLoadingMessage(chatId);
          setStatus({ status: 'idle' }, chatId);
          setLoading(false);
          setIsProcessing(false, chatId);
        }
      },
    }),
    [
      handleCloseHotelPanel,
      handleResponseComplete,
      closeStream,
      triggerSessionUpdate,
      addMessageToChat,
      setLatestErrorMessageId,
      clearLoadingMessage,
      setStatus,
      setLoading,
      setIsProcessing,
    ]
  );

  // SSE constants
  const sseConstants = useMemo(
    () => ({
      GUARDRAIL_ERROR_MESSAGE: GAURDRAIL_ERROR_MESSAGE,
      GUARDRAIL_LIMIT_EXCEEDED_MESSAGE,
      DATES,
    }),
    []
  );

  // SSEEventManager instance
  const sseEventManager = useMemo(
    () => new SSEEventManager(sseComponentCallbacks, sseRefs, sseConstants),
    [sseComponentCallbacks, sseRefs, sseConstants]
  );

  // Connect SSE stream for a specific chat using the new callback-based pattern
  const connectStream = useCallback(
    (chatId: string, streamUrl: string) => {
      // Abort existing connection if any
      try {
        tipAbortControllersByChatRef.current[chatId]?.abort();
      } catch {
        // ignore
      }

      tipConnectionStateByChatRef.current[chatId] = 'connecting';

      // Get lastEventId from chat session for reconnection (to avoid receiving duplicate events)
      const chatSession = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === chatId);
      const lastEventId = chatSession?.lastEventId;

      const ctrl = tipSSEClient!.connectStream(
        streamUrl,
        {
          onMessage: sseEventManager.createMessageHandler(chatId),
          onOpen: () => {
            tipConnectionStateByChatRef.current[chatId] = 'open';
            sseRetryAttemptRef.current[chatId] = false;
          },
          onError: () => {
            sseEventManager.handleError(chatId);
          },
          onClose: () => {
            tipConnectionStateByChatRef.current[chatId] = 'closed';
          },
          onUnauthorized: async () => {
            const chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === chatId);
            const isProcessing = chat?.isProcessing ?? false;

            if (!isProcessing) {
              tipConnectionStateByChatRef.current[chatId] = 'closed';
              return;
            }

            if (sseRetryAttemptRef.current[chatId]) {
              sseRetryAttemptRef.current[chatId] = false;
              pendingMessageByChatRef.current[chatId] = null;
              await handleUnauthorizedError();
              return;
            }

            sseRetryAttemptRef.current[chatId] = true;

            const pendingMessage = pendingMessageByChatRef.current[chatId];
            if (chat?.tipWorkflowId && tipSSEClient && pendingMessage) {
              try {
                const response = await tipSSEClient.sendMessage(
                  chat.tipWorkflowId,
                  pendingMessage.content,
                  { 'Content-Type': 'application/json' },
                  pendingMessage.uiContext
                );
                if (response?.newAccessToken) {
                  tipSSEClient.updateAuthToken(response.newAccessToken);
                }

                pendingMessageByChatRef.current[chatId] = null;
                connectStream(chatId, streamUrl);
              } catch {
                sseRetryAttemptRef.current[chatId] = false;
                pendingMessageByChatRef.current[chatId] = null;
                await handleUnauthorizedError();
              }
            } else {
              sseRetryAttemptRef.current[chatId] = false;
              pendingMessageByChatRef.current[chatId] = null;
              await handleUnauthorizedError();
            }
          },
        },
        lastEventId
      );

      tipAbortControllersByChatRef.current[chatId] = ctrl;
      return ctrl;
    },
    [tipSSEClient, sseEventManager]
  );

  // Eagerly initialize TIP session and stream connection for a chat
  const initializeTipSession = useCallback(
    async (chatId: string) => {
      if (!tipSSEClient || !chatId) return;

      // If already initializing, return the existing promise so callers can await it
      const existingPromise = tipInitializationPromiseByChatRef.current[chatId];
      if (existingPromise) {
        return existingPromise;
      }

      const chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === chatId);

      // Skip initialization for disabled chats (e.g., guardrail limit exceeded)
      if (chat?.isDisabled) {
        return;
      }

      // Skip if session already exists and stream is open
      const connectionState = tipConnectionStateByChatRef.current[chatId];
      if (chat?.tipWorkflowId && chat?.tipStreamUrl && connectionState === 'open') {
        return;
      }

      // Create the initialization promise
      const initPromise = (async () => {
        tipInitializingByChatRef.current[chatId] = true;

        try {
          // Create session if needed
          let wfId = chat?.tipWorkflowId;
          let streamUrl = chat?.tipStreamUrl;

          if (!wfId || !streamUrl) {
            const createSessionRes = await tipSSEClient.createSession();
            if (createSessionRes?.newAccessToken) {
              tipSSEClient.updateAuthToken(createSessionRes.newAccessToken);
            }
            wfId = createSessionRes.workflow_id;
            streamUrl = createSessionRes.stream_url;
            if (!wfId || !streamUrl) {
              return;
            }
            setChatTipSession(chatId, { workflowId: wfId, streamUrl });
          }

          // Connect stream if needed
          const currentState = tipConnectionStateByChatRef.current[chatId];
          if (currentState !== 'open' && currentState !== 'connecting') {
            connectStream(chatId, streamUrl);
          }

          // Pageload/eager init should wait briefly for open, but fail silently.
          const stateAfterConnect = tipConnectionStateByChatRef.current[chatId];
          if (stateAfterConnect !== 'open') {
            await waitForConnectionOpen(chatId, 15000);
          }
        } catch {
          // Ignore eager-initialization failures; send flow will retry initialization.
        } finally {
          tipInitializingByChatRef.current[chatId] = false;
        }
      })();

      tipInitializationPromiseByChatRef.current[chatId] = initPromise;

      try {
        await initPromise;
      } finally {
        tipInitializationPromiseByChatRef.current[chatId] = null;
      }
    },
    [tipSSEClient, setChatTipSession, connectStream, waitForConnectionOpen]
  );

  // Initialize TIP session when currentChatId changes (user switches chats, creates new chat, or on mount)
  // Also handles closing the previous chat's stream (immediately or deferred if processing)
  useEffect(() => {
    if (!hasHydrated || !currentChatId || !tipSSEClient) return;

    const previousChatId = previousChatIdRef.current;

    // Handle previous chat's stream closure
    if (previousChatId && previousChatId !== currentChatId) {
      // Clear all UI context when switching conversations - each conversation should have its own context
      uiContextManager.clearAllContext();

      // Close HQV modal when switching chats to prevent it from persisting across conversations
      hqvDispatch({ type: HQV_ACTIONS.MODAL_OPEN_STATE, payload: false });
      hqvDispatch({ type: HQV_ACTIONS.OPEN_GALLERY_ON_LOAD, payload: false });
      hqvDispatch({ type: HQV_ACTIONS.CACHED_PROPERTY_MEDIA, payload: undefined });
      setIsHotelPanelOpen(false);
      setSelectedHotelData(null);
      setSelectedPropertyId(null);
      uiContextManager.setHqvPanelOpen(false);

      // Read chatSessions from store to avoid dependency on the array
      const currentChatSessions = useMarriottAIPreviewStore.getState().chatSessions;
      const previousChat = currentChatSessions.find(c => c.id === previousChatId);
      const isPreviousChatProcessing = previousChat?.isProcessing ?? false;

      if (isPreviousChatProcessing) {
        // Defer stream closure until response completes
        // Keep the response timeout running so it can trigger if response never arrives
        pendingStreamClosuresRef.current.add(previousChatId);
      } else {
        // Clear response timeout for previous chat since we're closing the stream
        if (eventStreamResponseTimeoutByChatRef.current[previousChatId]) {
          clearTimeout(eventStreamResponseTimeoutByChatRef.current[previousChatId]);
          delete eventStreamResponseTimeoutByChatRef.current[previousChatId];
        }
        // Close stream immediately
        closeStream(previousChatId);
      }
    }

    // Update the previous chat ID ref
    previousChatIdRef.current = currentChatId;

    // Initialize the new chat's session
    initializeTipSession(currentChatId);
  }, [hasHydrated, currentChatId, tipSSEClient, initializeTipSession, closeStream, hqvDispatch]);

  // Sync ampRequest from viewData when conversation changes to ensure MiniSearchBar shows correct criteria
  useEffect(() => {
    if (!currentChatId) return;

    // Read the current viewData from the store
    const currentViewData = useMarriottAIPreviewStore.getState().currentState?.data;
    const viewDataAmpRequest = currentViewData?.ampRequest;
    // Extract isFlexDate from viewData to preserve flexible date state
    const isFlexDate = currentViewData?.isFlexDate === true;

    // If the viewData has an ampRequest, sync it to the search form store
    if (viewDataAmpRequest) {
      const ampRequestUpdates = buildStoreUpdatesFromAmpRequest(viewDataAmpRequest, isFlexDate);
      if (ampRequestUpdates) {
        setSearchFormState(ampRequestUpdates.keys, ampRequestUpdates.data);
      }
    }
  }, [currentChatId, setSearchFormState]);

  // On landing page (no currentChatId), find or create an empty session
  // createNewChat will reuse existing empty session if available (with TIP credentials from previous page load)
  // initializeTipSession will then only connect stream if tipWorkflowId already exists
  useEffect(() => {
    if (!hasHydrated || !tipSSEClient || currentChatId) {
      return;
    }
    createNewChat();
  }, [hasHydrated, tipSSEClient, currentChatId, createNewChat]);

  // Initialize inactivity timer to close stream after configured timeout of no user activity
  useEffect(() => {
    if (!tipSSEClient || !currentChatId) return;

    // Get timeout from env vars store, fallback to default
    const envTimeoutMs = useClientEnvVarsStore.getState().envVarsObject['SSE_INACTIVITY_TIMEOUT_MS'];
    const timeoutMs = envTimeoutMs ? Number(envTimeoutMs) : DEFAULT_INACTIVITY_TIMEOUT_MS;

    // Create and initialize the inactivity timer
    inactivityTimerRef.current = new InactivityTimer({
      timeoutMs,
      onTimeout: () => {
        // Close current chat's stream on inactivity
        if (currentChatId) {
          closeStream(currentChatId);
        }
      },
    });

    inactivityTimerRef.current.initialize();

    return () => {
      inactivityTimerRef.current?.cleanup();
      inactivityTimerRef.current = null;
    };
  }, [tipSSEClient, currentChatId, closeStream]);

  // Handle input change
  // WEB-193725: Track raw input separately to fix incremental typing redaction.
  // Problem: When user types "Account: 12345678" it redacts to "****5678".
  // If user then types "9", the input becomes "****56789" (5 digits visible!)
  // because we were appending to the redacted text, not the raw input.
  // Solution: Track raw input in a ref, always redact from raw, display redacted.
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDisplayValue = e.target.value;
    const previousDisplayValue = inputValue || '';
    const previousRawValue = rawInputRef.current;
    const cursorPosition = e.target.selectionStart;

    // Determine what changed using cursor position for accurate mid-text edits
    let newRawValue: string;

    if (newDisplayValue.length === 0) {
      // User cleared the input
      newRawValue = '';
    } else if (previousDisplayValue === previousRawValue) {
      // No redaction active - display and raw are in sync, use new value directly
      newRawValue = newDisplayValue;
    } else {
      // Redaction is active - need to carefully reconstruct raw value
      // Use cursor position to determine where the edit occurred
      const lengthDiff = newDisplayValue.length - previousDisplayValue.length;

      if (lengthDiff > 0) {
        // Characters were added
        // The new characters are at position (cursorPosition - lengthDiff) to cursorPosition
        const insertPosition = cursorPosition - lengthDiff;
        const addedChars = newDisplayValue.slice(insertPosition, cursorPosition);
        newRawValue = previousRawValue.slice(0, insertPosition) + addedChars + previousRawValue.slice(insertPosition);
      } else if (lengthDiff < 0) {
        // Characters were removed
        // The removed characters were at position cursorPosition to (cursorPosition - lengthDiff)
        const deleteCount = -lengthDiff;
        newRawValue = previousRawValue.slice(0, cursorPosition) + previousRawValue.slice(cursorPosition + deleteCount);
      } else {
        // Same length but different content (e.g., select and replace with same length text)
        // Fall back to using display value as raw since we can't reliably track the change
        newRawValue = newDisplayValue;
      }
    }

    // Update raw input ref
    rawInputRef.current = newRawValue;

    // Always redact from the raw input
    const result = redact(newRawValue);
    const displayValue = result.containsSensitiveData ? result.redactedText : newRawValue;

    if (result.containsSensitiveData) {
      // PII found — set flag immediately
      setHasPiiDetected(true);
    } else if (!newRawValue) {
      // Input fully cleared — safe to reset the flag
      setHasPiiDetected(false);
    }
    setStoreInputValue(displayValue);
    setLocalInputValue(displayValue);
  };

  // Clear input helper
  const clearInput = useCallback(() => {
    rawInputRef.current = ''; // WEB-193725: Reset raw input tracking
    setStoreInputValue('');
    setLocalInputValue('');
    setHasPiiDetected(false);
  }, [setStoreInputValue]);

  // Handle cancel request
  const handleCancelRequest = () => {
    // Clear response timeout timer
    if (currentChatId && eventStreamResponseTimeoutByChatRef.current[currentChatId]) {
      clearTimeout(eventStreamResponseTimeoutByChatRef.current[currentChatId]);
      delete eventStreamResponseTimeoutByChatRef.current[currentChatId];
    }

    // Interrupt TIP and null the guard ref so stale events are ignored
    if (currentChatId) {
      const chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === currentChatId);
      if (chat?.tipWorkflowId && tipSSEClient) {
        tipSSEClient.interruptSession(chat.tipWorkflowId);
      }
    }

    // Use store's cancelRequest method for state management
    cancelRequest();
  };

  // Main send message handler - uses pre-initialized session/stream with fallback
  const handleSendMessage = useCallback(
    async (messageOverride?: string, isRetry?: boolean) => {
      const USE_CHAT_MOCK_MODEL = useClientEnvVarsStore.getState().envVarsObject['USE_CHAT_MOCK_MODEL'];
      if (USE_CHAT_MOCK_MODEL === 'true') {
        const mockResponse = chatResultsMock;
        handleAPIResponse(mockResponse as AgentExecutionResult);
        return;
      }
      const trimmedOverride = messageOverride?.trim();
      const usesOverride = Boolean(trimmedOverride);
      const messageToSend = usesOverride ? trimmedOverride : inputValue?.trim();
      if (!messageToSend || isLoading || isProcessing || !tipSSEClient) return;
      if (isChatDisabled) return;
      // Enter path: PII flag comes from the input pipeline (redact in onChange). Override path
      // (chips, retry): evaluate redact on the outgoing text only so textarea state does not block pills.
      const overridePiiResult = usesOverride ? redact(messageToSend) : null;
      const shouldBlockPii = usesOverride ? Boolean(overridePiiResult?.containsSensitiveData) : hasPiiDetected;
      const userMessageContentForPiiBranch =
        usesOverride && overridePiiResult?.containsSensitiveData ? overridePiiResult.redactedText : messageToSend;

      if (shouldBlockPii) {
        const piiTurnTs = Date.now();
        const userMessageId = String(piiTurnTs);
        const piiErrorId = `${piiTurnTs}-pii-error`;
        const isFollowUpTurn = messages.length > 0;
        // Same pin-to-top behavior as a normal send; early return skipped prepareActiveMessageScroll before.
        prepareActiveMessageScroll(userMessageId, isFollowUpTurn);
        addMessage({
          id: userMessageId,
          content: userMessageContentForPiiBranch,
          isUser: true,
          timestamp: new Date(),
        });
        clearInput();
        addMessage({
          id: piiErrorId,
          content: PII_DETECTED_ERROR_MESSAGE,
          isUser: false,
          timestamp: new Date(),
          toolsUsed: [],
          uiType: 'error',
          uiData: {
            errorText: PII_DETECTED_ERROR_MESSAGE,
            variant: 'info',
          },
        });
        return;
      }

      // Ensure we have a chat session BEFORE adding message
      // This is critical because addMessage() requires currentChatId to exist
      let chatId = currentChatId;

      // Check if we need to auto-delete oldest chat before this message creates a new non-empty chat
      const chatSessionsState = useMarriottAIPreviewStore.getState().chatSessions;
      const currentChat = chatId ? chatSessionsState.find(c => c.id === chatId) : null;
      const isCurrentChatEmpty = !currentChat || currentChat.messageCount === 0;
      const nonEmptyChats = chatSessionsState.filter(chat => chat.messageCount > 0);
      const maxChatCount = Number(useClientEnvVarsStore.getState().envVarsObject['MAX_CHAT_COUNT']) || MAX_CHAT_COUNT;

      // If current chat is empty (will become non-empty after this message) and we're at max capacity, delete oldest
      if (isCurrentChatEmpty && nonEmptyChats.length >= maxChatCount) {
        const oldestChat = [...nonEmptyChats].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )[0];
        if (oldestChat) {
          useMarriottAIPreviewStore.getState().deleteChat(oldestChat.id);
        }
      }

      if (!chatId) {
        chatId = createNewChat();
      }

      // At this point chatId is guaranteed to be a string (either from currentChatId or createNewChat)
      const activeChatId = chatId as string;

      // ── UI Context Syncing ────────────────────────────────────────────────
      // search_options replicates the exact NLSSearchOptions payload sent by
      // buildNLSSearchVariables in nlsSearchService.ts — same field names,
      // same derivation logic, same constants.
      const roomsCount = Math.max(parseInt(String(searchFormRooms?.numRooms ?? 1), 10) || 1, 1);
      const adultsPerRoom = searchFormRooms?.numAdultsPerRoom ?? 1;
      const childrenPerRoom = searchFormRooms?.numChildrenPerRoom ?? 0;
      const childrenAges: number[] = searchFormRooms?.childrenAges ?? [];
      const childAges: number[] | null =
        childrenPerRoom > 0 && childrenAges.length > 0
          ? childrenAges.slice(0, childrenPerRoom * roomsCount).map((age: any) => {
              if (age === null || age === undefined) return 0;
              const parsed = parseInt(String(age), 10);
              return isNaN(parsed) ? 0 : parsed;
            })
          : null;

      const { options: rateOptions } = buildSearchQueryDataFromStore(searchFormSpecialRates, searchFormUsePoints);

      // Get customerId from store for authenticated users
      const customerId = useMarriottAIPreviewStore.getState().consumerId ?? null;

      const searchOptions = {
        startDate: searchFormDates?.fromDate ? moment(searchFormDates.fromDate).format('YYYY-MM-DD') : null,
        endDate: searchFormDates?.toDate ? moment(searchFormDates.toDate).format('YYYY-MM-DD') : null,
        isFlexDate: searchFormDates?.flexible === 'true' || searchFormDates?.flexible === true,
        quantity: roomsCount,
        numberInParty: Math.max(adultsPerRoom + childrenPerRoom, 1),
        childAges,
        rateRequestTypes: rateOptions.rateRequestTypes,
        includeMandatoryFees: true,
        includeTaxesAndFees: false,
        includeUnavailableProperties: true,
        customerId,
      };

      const existingContext = uiContextManager.getStateForMessage();

      if (existingContext) {
        // An existing context (e.g. property_details_view, search_results_sorted)
        // is already set — append search_options into its data without overriding
        // the richer context type.
        uiContextManager.setState(existingContext.type, {
          ...existingContext.data,
          search_options: searchOptions,
        });
      }

      // Consume the context for this message.
      // This will:
      // 1. Return null if no relevant context (user hasn't modified search, HQV not open)
      // 2. Return property_details_view only if HQV panel is open
      // 3. Return search_results_filtered only if user explicitly modified search via NLS
      // 4. Clear the search modified flag after consuming (so next message won't resend)
      const uiContext = uiContextManager.consumeContextForMessage();

      const userMessage = {
        id: Date.now().toString(),
        content: messageToSend,
        isUser: true,
        timestamp: new Date(),
      };
      if (!isRetry) {
        if (chatId) {
          setLatestErrorMessageId(chatId, null);
        }
        const isFollowUpTurn = messages.length > 0;
        prepareActiveMessageScroll(userMessage.id, isFollowUpTurn);
        addMessage(userMessage);
        clearInput();
      }
      // Pass chatId to set processing state on the specific chat
      setIsProcessing(true, chatId);
      clearLoadingMessage(chatId);
      enqueueLoadingMessage('DEFAULT_THINKING', chatId);
      setStatus({ status: 'thinking', action: 'Finding the best options for you.' }, chatId);

      // Clear any existing timeout for this chat and start new response timeout timer
      if (eventStreamResponseTimeoutByChatRef.current[activeChatId]) {
        clearTimeout(eventStreamResponseTimeoutByChatRef.current[activeChatId]);
      }
      const envTimeoutMs = useClientEnvVarsStore.getState().envVarsObject['EVENT_STREAM_RESPONSE_TIMEOUT_MS'];
      const timeoutMs = envTimeoutMs ? Number(envTimeoutMs) : DEFAULT_EVENT_STREAM_RESPONSE_TIMEOUT_MS;
      eventStreamResponseTimeoutByChatRef.current[activeChatId] = window.setTimeout(() => {
        handleResponseTimeout(activeChatId, messageToSend);
      }, timeoutMs);

      try {
        // Reset per-send buffers for this chat
        tipPendingPreviewByChatRef.current[chatId] = null;
        tipFormattedResponseByChatRef.current[chatId] = null;

        // Store pending message for potential 401 retry
        pendingMessageByChatRef.current[chatId] = {
          content: userMessage.content,
          uiContext,
        };

        // Wait for any ongoing initialization to complete first
        const pendingInit = tipInitializationPromiseByChatRef.current[chatId];
        if (pendingInit) {
          await pendingInit;
        }

        // Get session info - should already be initialized eagerly
        let chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === chatId);
        let wfId = chat?.tipWorkflowId;
        let connectionState = tipConnectionStateByChatRef.current[chatId];

        // Fallback: only initialize if NO initialization was pending (cold start scenario)
        if (!pendingInit && (!wfId || connectionState === 'closed' || !connectionState)) {
          await initializeTipSession(chatId);
          // Re-fetch after initialization
          chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === chatId);
          wfId = chat?.tipWorkflowId;
          connectionState = tipConnectionStateByChatRef.current[chatId];
        }

        if (!wfId || connectionState !== 'open') {
          throw new Error('Failed to initialize TIP session');
        }

        // Wait for stream to be open if still connecting
        if (connectionState !== 'open') {
          await waitForConnectionOpen(chatId);
        }

        // Check if message was already sent during 401 retry in onUnauthorized
        if (!pendingMessageByChatRef.current[chatId]) {
          return;
        }

        // Send the message – attach ui_context when available
        const messageResponse = await tipSSEClient.sendMessage(
          wfId,
          userMessage.content,
          { 'Content-Type': 'application/json' },
          uiContext
        );
        if (messageResponse?.newAccessToken) {
          tipSSEClient.updateAuthToken(messageResponse.newAccessToken);
        }

        // Clear pending message after successful send
        pendingMessageByChatRef.current[chatId] = null;
      } catch (error) {
        // Clear pending message on error
        pendingMessageByChatRef.current[chatId] = null;

        // Clear response timeout timer on error
        if (chatId && eventStreamResponseTimeoutByChatRef.current[chatId]) {
          clearTimeout(eventStreamResponseTimeoutByChatRef.current[chatId]);
          delete eventStreamResponseTimeoutByChatRef.current[chatId];
        }

        const isGuardrailViolation = error instanceof GuardrailViolationError;

        if (isGuardrailViolation) {
          // Handle guardrail violation - same as SSE guardrail_blocked event
          const apiResult: AgentExecutionResult = {
            intent: 'guardrail_blocked',
            toolsUsed: [],
            response: GAURDRAIL_ERROR_MESSAGE,
            chatId: chatId,
            uiData: { content: GAURDRAIL_ERROR_MESSAGE },
            uiType: 'default',
            metadata: { executionTime: 0 },
          };
          handleAPIResponse(apiResult);
        } else {
          // Generic error handling with retry option
          const errorId = Date.now().toString() + '-error';
          const errorMessage = {
            id: errorId,
            content: 'Something went wrong.',
            isUser: false,
            timestamp: new Date(),
            toolsUsed: [],
            uiType: 'error',
            uiData: {
              errorText: 'Something went wrong.',
              originalMessage: messageToSend,
              errorMessageId: errorId,
            },
          };
          addMessage(errorMessage);
          if (chatId) {
            setLatestErrorMessageId(chatId, errorId);
          }
        }

        // Pass chatId to clear processing state on the specific chat
        clearLoadingMessage(chatId);
        setStatus({ status: 'idle' }, chatId);
        setLoading(false);
        setIsProcessing(false, chatId);
      } finally {
        inputRef.current?.focus();
      }
    },
    [
      inputValue,
      hasPiiDetected,
      isLoading,
      isProcessing,
      tipSSEClient,
      currentChatId,
      addMessage,
      clearInput,
      setLoading,
      setIsProcessing,
      setStatus,
      enqueueLoadingMessage,
      clearLoadingMessage,
      createNewChat,
      setLatestErrorMessageId,
      initializeTipSession,
      waitForConnectionOpen,
      messages.length,
      prepareActiveMessageScroll,
      searchFormDates,
      searchFormRooms,
      searchFormSpecialRates,
      searchFormUsePoints,
      isChatDisabled,
      handleResponseTimeout,
    ]
  );

  // Handle key press - Enter sends message
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && inputValue?.length) {
      e.preventDefault();
      // The Adobe Launch click-track rule listens for `mousedown` on elements carrying
      // `data-custom_click_track_value`, NOT `click`. Pressing Enter never produces a mousedown,
      // so we dispatch one on the attributed send button to fire the same AI-search click-track
      // (event207) as a real button press. `.click()` then runs the send (handleActionClick).
      const button = iconRef.current;
      if (button) {
        button.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
      }
      iconRef.current?.click();
      iconRef.current?.blur();
    }
  };

  // Handle action button click (send/stop)
  const handleActionClick = () => {
    if (isProcessing) {
      handleCancelRequest();
    } else if (inputValue?.length) {
      handleSendMessage();
    }
  };

  // Determine which icon to show
  const getActionIcon = (): string => {
    if (isProcessing) return 'icon-stop';
    if (inputValue?.length) return 'icon-send-fill';
    return 'icon-microphone';
  };

  // All suggestion pills/chips: send immediately (same as Enter); no prefill for editing.
  const handleChipClick = useCallback(
    (chipText: string) => {
      if (!chipText) return;
      const trimmed = chipText.trim();
      if (!trimmed) return;
      void handleSendMessage(trimmed);
    },
    [handleSendMessage]
  );

  // Handle retry - resends the original message that failed
  const handleRetry = useCallback(
    (originalMessage: string, errorMessageId: string) => {
      if (!originalMessage || !errorMessageId) return;
      // Remove the error message before retrying
      removeMessage(errorMessageId);
      // Retry sending the message (isRetry=true skips adding duplicate user message)
      handleSendMessage(originalMessage, true);
    },
    [handleSendMessage, removeMessage]
  );

  // Reuse shop map's built-in list CTA behavior
  const handleViewAllProperties = useCallback(
    (_messageViewData?: any) => {
      // Close the hotel panel when viewing all properties
      handleCloseHotelPanel();

      // Update store data for the clicked message's search criteria (mini-search + filters).
      // Use updateViewData (partial merge) intentionally here — we do NOT want to change
      // currentState.id because that is used as the key for SearchResultsComponent.
      // Changing the key would unmount/remount the shop component, causing it to
      // re-initialize with isMapViewEnabled=true and miss the view-mode event below.
      if (_messageViewData) {
        // Ensure isSinglePropertyResponse is explicitly set to false if not present
        // to clear any previous single property state from availability_calendar_search
        const viewDataWithSinglePropertyFlag = {
          ..._messageViewData,
          isSinglePropertyResponse: _messageViewData.isSinglePropertyResponse ?? false,
        };
        updateViewData(viewDataWithSinglePropertyFlag);

        // Sync the message's ampRequest to the search form store so MiniSearchBar
        // and rates display use the correct search criteria for this message
        const messageAmpRequest = _messageViewData.ampRequest;
        // Extract isFlexDate from viewData to preserve flexible date state
        const isFlexDate = _messageViewData.isFlexDate === true;
        if (messageAmpRequest) {
          const ampRequestUpdates = buildStoreUpdatesFromAmpRequest(messageAmpRequest, isFlexDate);
          if (ampRequestUpdates) {
            setSearchFormState(ampRequestUpdates.keys, ampRequestUpdates.data);
          }
        } else if (isFlexDate !== undefined) {
          // Even without ampRequest, update the flexible flag if isFlexDate is available
          const ampRequestUpdates = buildStoreUpdatesFromAmpRequest(null, isFlexDate);
          if (ampRequestUpdates) {
            setSearchFormState(ampRequestUpdates.keys, ampRequestUpdates.data);
          }
        }
      }
      // Also ensure viewMode is grid in the store.
      updateViewMode('grid');

      // On mobile, switch from chat pane to results pane.
      if (isMobile) {
        setShowChat(false);
      }

      // Defer the event by one tick so it fires AFTER React has committed any
      // pending re-renders. The shop's SearchResultsComponent registers its
      // 'chat-view-mode-change' listener inside a useEffect — dispatching
      // synchronously would fire before that effect runs on a freshly mounted
      // instance and the event would be lost.
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('chat-view-mode-change', { detail: { mode: 'grid' } }));
        }, 0);
      }
    },
    [updateViewData, updateViewMode, isMobile, setShowChat, setSearchFormState, handleCloseHotelPanel]
  );

  // Render chip group component
  const renderChipGroup = () => {
    if (!mChipGroup?.mQuickSelectChipMulti || mChipGroup.mQuickSelectChipMulti.length === 0) {
      return null;
    }

    return (
      <Styled.ChipGroupWrapper $isNewChatMode={isNewChatMode}>
        <Styled.ChipMultifield $isNewChatMode={isNewChatMode}>
          {mChipGroup.mQuickSelectChipMulti.map((chip, index) => (
            <Styled.ChipItem
              key={`chip-${index}`}
              type="button"
              data-track-label={AI_SEARCH_TRACK_LABELS.SUGGESTED_SEARCH_CHIPS}
              onClick={() => {
                const chipText = chip?.aBody2?.bodyText ?? chip?.aBody?.bodyText ?? '';
                handleChipClick(chipText);
              }}
              className="custom_click_track"
              data-custom_click_track_value={AI_SEARCH_CLICK_TRACK.SUGGESTED_SEARCH_PILL(chip.aBody.bodyText, index)}
            >
              <Styled.ChipText>{chip.aBody.bodyText}</Styled.ChipText>
            </Styled.ChipItem>
          ))}
        </Styled.ChipMultifield>
      </Styled.ChipGroupWrapper>
    );
  };

  const renderDisclaimer = (
    paragraph: OChatParagraphProps | undefined,
    options: {
      layout: ChatDisclaimerLayout;
      className?: string;
      disclaimerContext?: ChatDisclaimerContext;
      onDismiss?: () => void;
    }
  ) => (
    <ChatDisclaimer
      className={options.className}
      enabled={paragraph?.aBody?.enabled}
      text={paragraph?.aBody?.bodyText}
      disclaimerContext={options.disclaimerContext ?? 'chat'}
      layout={options.layout}
      onDismiss={options.onDismiss}
    />
  );

  const isWelcomeScreen = messages.length === 0;

  // Render chat field component
  const renderChatField = (
    refToUse: React.RefObject<HTMLTextAreaElement> = inputRef,
    trackLabel?: string,
    accessibleLabel?: string
  ) => {
    if (!mFieldChat) {
      return null;
    }

    const placeholder = isChatDisabled
      ? CHAT_DISABLED_PLACEHOLDER
      : isProcessing
      ? 'Processing your request...'
      : mFieldChat.aInputText?.text || 'Where can we take you?';

    const actionIcon = getActionIcon();

    // Only add click tracking when sending a message (not when stopping)
    const shouldTrackClick = !isProcessing && inputValue?.length > 0;

    // Show icon only when there's input or when processing (to show stop button)
    const showIcon = isProcessing || inputValue?.length > 0;
    const iconButtonAriaLabel = isProcessing ? 'Stop conversation' : 'Send message';

    return (
      <Styled.ChatFieldWrapper>
        <MFieldChat
          inputRef={refToUse}
          iconRef={iconRef}
          aInputText={{
            text: placeholder,
            ...(accessibleLabel ? { label: accessibleLabel } : {}),
            value: inputValue,
            onChange: handleInputChange,
            onKeyDown: handleKeyPress,
            disabled: isLoading || isProcessing || isChatDisabled,
            maxLength: maxLength,
          }}
          aIcon={
            showIcon
              ? {
                  icon: actionIcon,
                  size: AIconSize.M,
                }
              : undefined
          }
          iconButtonAriaLabel={showIcon ? iconButtonAriaLabel : undefined}
          onIconClick={handleActionClick}
          clickTrackClass={shouldTrackClick ? 'custom_click_track' : undefined}
          clickTrackValue={shouldTrackClick ? AI_SEARCH_CLICK_TRACK.SEARCH_WITH_AI : undefined}
          trackLabel={isProcessing ? AI_SEARCH_TRACK_LABELS.STOP_CONVERSATION : trackLabel}
          isDisabled={isChatDisabled} //to apply custom styles to input field in disbaled state(other than processing state)
        />
        {(messages.length > 0 || isNewChatMode) && renderDisclaimer(mParagraph2, { layout: 'desktop-under-input' })}
      </Styled.ChatFieldWrapper>
    );
  };

  const welcomeDisclaimerEnabled = mParagraph?.aBody?.enabled !== false;
  const showWelcomeMobileDisclaimer =
    messages.length === 0 &&
    !isNewChatMode &&
    showChat &&
    !welcomeMobileDisclaimerDismissed &&
    welcomeDisclaimerEnabled;

  // Calculate whether page scroll should be enabled on mobile
  // Page scroll is enabled for: Chat tab OR Results tab in list/grid view
  // Page scroll is disabled for: Results tab in map view
  const enablePageScroll = showChat || viewMode !== 'map';

  return (
    <HQVContext.Provider value={{ hqvState, hqvDispatch }}>
      <ChatFiltersGlobalStyle />
      <Styled.Wrapper
        data-component-name="o-mi-chat-components-OChat"
        data-testid={TEST_ID}
        data-componentid={componentId}
        className={clsx(className)}
        $isSplitView
        $enablePageScroll={enablePageScroll}
        aria-hidden={isMobile && isSidebarOpen ? true : undefined}
      >
        {/* Split view - side by side layout */}
        <Styled.SplitMainContent $enablePageScroll={enablePageScroll}>
          <Styled.MobileHeaderWrapper data-chat-header="mobile">
            <UnifiedHeader
              currentChatName={currentChatName}
              currentChatId={currentChatId}
              onToggleSidebar={handleToggleSidebar}
              menuOnly={messages.length === 0 && !isNewChatMode}
              showChat={showChat}
              onViewToggle={handleMobileToggle}
              onRenameChat={handleRenameChat}
              onDeleteChat={handleDeleteChat}
              onNewChat={() => handleNewChat()}
            />
          </Styled.MobileHeaderWrapper>

          {/* Content area - Chat and Preview side by side */}
          <Styled.ContentArea $enablePageScroll={enablePageScroll}>
            {/* Left side - Chat section */}
            <Styled.ChatSection $showChat={showChat} $isNewChatMode={isNewChatMode && messages.length === 0}>
              {(isNewChatMode || messages.length > 0) && (
                <Styled.DesktopHeaderWrapper>
                  <UnifiedHeader
                    currentChatName={currentChatName}
                    currentChatId={currentChatId}
                    onToggleSidebar={handleToggleSidebar}
                    showChat={showChat}
                    onViewToggle={handleMobileToggle}
                    onRenameChat={handleRenameChat}
                    onDeleteChat={handleDeleteChat}
                    onNewChat={handleNewChat}
                  />
                </Styled.DesktopHeaderWrapper>
              )}
              {isNewChatMode &&
                messages.length === 0 &&
                renderDisclaimer(mParagraph2, {
                  layout: 'mobile-top-bar',
                  className: 'o-chat-mobile-top-disclaimer',
                })}
              {/* Messages area */}
              <Styled.MessagesContainer
                ref={messagesContainerRef}
                $isEmpty={messages.length === 0}
                $isNewChatMode={isNewChatMode}
              >
                {messages.length > 0 &&
                  renderDisclaimer(mParagraph2, {
                    layout: 'mobile-messages-inline',
                    className: 'o-chat-mobile-top-disclaimer',
                  })}
                {/* Welcome content - show when no messages */}
                {messages.length === 0 && (
                  <Styled.WelcomeSection $isNewChatMode={isNewChatMode}>
                    {/* Lottie sparkles animation - Only show if NOT new chat mode */}
                    {!isNewChatMode && (
                      <Styled.ImageWrapper>
                        <ChatSparklesLottie size={100} />
                      </Styled.ImageWrapper>
                    )}

                    {/* Text Block - Header and Body */}
                    {mTextBlockSimple && (
                      <Styled.TextBlockWrapper className={alignmentClass} $isNewChatMode={isNewChatMode}>
                        {mTextBlockSimple.aEyebrow?.eyebrowText && (
                          <Styled.EyebrowWrapper>
                            <AEyebrow variant={mTextBlockSimple.aEyebrow.eyebrowVariant}>
                              {mTextBlockSimple.aEyebrow.eyebrowText}
                            </AEyebrow>
                          </Styled.EyebrowWrapper>
                        )}

                        {mTextBlockSimple.aHeader?.text && (
                          <Styled.HeaderWrapper $isNewChatMode={isNewChatMode}>
                            <AHeader
                              text={mTextBlockSimple.aHeader.text}
                              size={mTextBlockSimple.aHeader.size || AHeaderSize.TitleS}
                              element={mTextBlockSimple.aHeader.element || AHeaderTag.h2}
                            />
                          </Styled.HeaderWrapper>
                        )}

                        {mTextBlockSimple.aBody?.bodyText && (
                          <Styled.BodyWrapper $isNewChatMode={isNewChatMode}>
                            <ABody
                              bodyText={mTextBlockSimple.aBody.bodyText}
                              size={mTextBlockSimple.aBody.size || ABodySize.M}
                              color={mTextBlockSimple.aBody.color || ABodyColor.Default}
                            />
                          </Styled.BodyWrapper>
                        )}
                      </Styled.TextBlockWrapper>
                    )}
                    {/* Desktop Chat field in center for first time users*/}
                    {!isNewChatMode && (
                      <Styled.DesktopWelcomeInputWrapper>
                        {renderChatField(
                          inputRef,
                          isWelcomeScreen && !isNewChatMode && !isMobile
                            ? AI_SEARCH_TRACK_LABELS.SEND_MESSAGE_CTA
                            : undefined
                        )}
                      </Styled.DesktopWelcomeInputWrapper>
                    )}

                    {/* Chips */}
                    {renderChipGroup()}
                  </Styled.WelcomeSection>
                )}

                {/* Chat messages */}
                {messages.map(message => (
                  <div
                    key={message.id}
                    ref={(node: HTMLDivElement | null) => {
                      setMessageNodeRef(message.id, node);
                    }}
                  >
                    <ChatMessage
                      message={message}
                      chatId={currentChatId}
                      onThumbFeedback={handleThumbFeedback}
                      onNavigate={handleNavigateToMessage}
                      onOpenHotelPanel={handleOpenHotelPanel}
                      onChipClick={handleChipClick}
                      onViewAllProperties={handleViewAllProperties}
                      key={message.id}
                      onRetry={message.id === latestErrorMessageId ? handleRetry : undefined}
                    />
                  </div>
                ))}
                {/* Status indicator in chat flow, below latest bubble */}
                {status.status !== 'idle' && (
                  <StatusIndicator loadingMessage={loadingMessage}>{status.action}</StatusIndicator>
                )}
                {activeMessageSpacerHeight > 0 && <div style={{ minHeight: `${activeMessageSpacerHeight}px` }} />}
              </Styled.MessagesContainer>
              <Styled.ScrollToBottomButton
                className="d-flex justify-content-center align-items-center"
                type="button"
                aria-label="Scroll to bottom"
                aria-hidden={!showScrollToBottom}
                title="Scroll to bottom"
                tabIndex={showScrollToBottom ? 0 : -1}
                $isVisible={showScrollToBottom}
                onClick={scrollToBottom}
              >
                <AIcon icon="icon-down-arrow" size={AIconSize.L} aria-hidden="true" />
              </Styled.ScrollToBottomButton>

              {showWelcomeMobileDisclaimer &&
                renderDisclaimer(mParagraph, {
                  layout: 'dismissible-welcome-mobile',
                  className: 'o-chat-welcome-mobile-disclaimer',
                  disclaimerContext: 'welcome',
                  onDismiss: () => setWelcomeMobileDisclaimerDismissed(true),
                })}

              {/* Chat input - at bottom of chat section, hidden on desktop during welcome state */}
              <Styled.ChatInputContainer $isWelcome={messages.length === 0 && !isNewChatMode}>
                {renderChatField(
                  mobileInputRef,
                  isWelcomeScreen && (isNewChatMode || isMobile) ? AI_SEARCH_TRACK_LABELS.SEND_MESSAGE_CTA : undefined,
                  isWelcomeScreen ? 'Where can we take you' : undefined
                )}
              </Styled.ChatInputContainer>
              {messages.length === 0 &&
                !isNewChatMode &&
                welcomeDisclaimerEnabled &&
                renderDisclaimer(mParagraph, {
                  layout: 'welcome-desktop-footer',
                  disclaimerContext: 'welcome',
                })}
            </Styled.ChatSection>

            {/* Right side - Preview section */}
            <Styled.PreviewSection $showChat={showChat} $enablePageScroll={enablePageScroll}>
              {/*
                Both MapPlaceholder and PreviewPanel are always mounted for smooth transitions.
                We toggle visibility using $hidden prop instead of conditional rendering.
                This eliminates the blank screen delay when switching to "New Chat" mode
                because the map component stays mounted and ready.
              */}
              <Styled.MapPlaceholder
                $hidden={isNewChatMode || messages.length > 0 || currentView?.viewType !== 'empty'}
              >
                <img
                  src={emptyStatePreviewImageSrc}
                  alt="Discover amazing destinations"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Styled.MapPlaceholder>
              <Styled.PreviewPanelWrapper
                $hidden={!isNewChatMode && messages.length === 0 && currentView?.viewType === 'empty'}
              >
                <PreviewPanel
                  currentView={currentView}
                  viewData={viewData}
                  viewMode={viewMode}
                  uiData={messages[messages.length - 1]?.uiData}
                  hoveredHotelId={hoveredHotelId}
                  onViewToggle={handleViewToggle}
                  onHotelHover={setHoveredHotelId}
                  onOpenHotelPanel={handleOpenHotelPanel}
                  onPropertyMediaLoaded={handlePropertyMediaLoaded}
                  renderEmptyState={renderEmptyState}
                  isStaticMap={isNewChatMode && messages.length === 0}
                  isMapLoading={isProcessing}
                  miniSearchBarModel={miniSearchBarModel}
                  isMiniSearchLoading={isLoading}
                  llmMetadata={llmMetadata}
                  onNLSSearchComplete={handleNLSSearchComplete}
                />
              </Styled.PreviewPanelWrapper>
            </Styled.PreviewSection>
          </Styled.ContentArea>
        </Styled.SplitMainContent>
        {responsiveGridModelHotelDetails && responsiveGridModelPropsHotelDetails && (
          <HotelDetailsPanel
            key={selectedPropertyId || ''}
            isMobile={isMobile}
            isOpen={isHotelPanelOpen}
            onClose={handleCloseHotelPanel}
            propertyData={selectedHotelData}
            propertyId={selectedPropertyId || ''}
            responsiveGridModelProps={responsiveGridModelPropsHotelDetails}
            isSinglePropertyResponse={selectedIsSinglePropertyResponse}
          />
        )}
      </Styled.Wrapper>
    </HQVContext.Provider>
  );
};

export type { OChatProps } from './OChat.types';
export { OChatAlignment, TEST_ID as O_CHAT_TEST_ID } from './OChat.types';

// Export the cancel request handler for external use
export const useCancelRequest = () => {
  const { cancelRequest } = useAPIActions();

  return React.useCallback(() => {
    // Interrupt TIP if available (requires TipSSEClient instance)
    // if (currentChatId) {
    //   const chat = useMarriottAIPreviewStore.getState().chatSessions.find(c => c.id === currentChatId);
    //   // Note: TIP interruption needs to be handled by components that have access to tipSSEClient
    // }

    // Use store's cancelRequest method for state management
    cancelRequest();
  }, [cancelRequest]);
};

const OChatConfig = {
  emptyLabel: 'OChat',
  isEmpty: () => true,
  resourceType: 'mi-aem-utils/components/content/chat/o-chat',
};
const OchatEditable = (props: OChatProps) => {
  return (
    <EditableComponent config={OChatConfig} {...props}>
      <OChat {...props} />
    </EditableComponent>
  );
};

export default OchatEditable;
